interface ArrayDifference {
  oldValuesArray: object[];
  newValuesArray: object[];
}

export interface IChangeDetails {
  property?: object | string;
  originalValue?: object | string;
  newValue?: object | string;
  operation?: string;
}

export interface IChanges {
  changeType: string;
  changes: string;
}

export interface ILogData {
  changeLogName: string;
  modifiedOn: string;
  changedBy: string;
  changeDetails: IChanges[];
}

import { isEqual, isObject } from 'lodash-es';
import moment from 'moment';

const NS = 'CompareObjectsService'; // jshint ignore:line

class CompareObjectsService {
  // Method to compare the difference in array value.  If value of array is an object its not compared here
  // as the same is handled in the calling methods. Only array was values will be compared.
  private arrayDiff<T>(arr1: object[], arr2: object[]): ArrayDifference {
    const diff: ArrayDifference = {
      newValuesArray: [],
      oldValuesArray: [],
    };

    //Identify all the values which are present in old array and not present in new one
    diff.oldValuesArray = arr1.filter(function (value) {
      if (Object(value) === value) {
        // Ignore looping through objects as they are handled when the values changes in main methods
      } else if (value && arr2.indexOf(value) === -1) {
        return value;
      }

      return null;
    });

    // Identify all the values which are present in new array and not present in old one
    diff.newValuesArray = arr2.filter(function (value) {
      if (Object(value) === value) {
        // Ignore looping through objects as they are handled when the values changes in main methods
      } else if (value && arr1.indexOf(value) === -1) {
        return value;
      }

      return null;
    });

    return diff;
  }

  private isObject(x: any): boolean {
    return Object(x) === x;
  }

  private isDate(x: any): boolean {
    return x instanceof Date && !isNaN(x.getTime());
  }

  /**
   * Compares 2 objects
   * @param oldObj
   * @param newObj
   * @param keyName
   * @param computeArrayDiff - this only compares shallow primitive types in arrays, not objects
   * @param sectionsToIgnore
   */
  public computeObjectDifference(
    oldObj: any,
    newObj: any,
    keyName: string,
    computeArrayDiff: boolean,
    sectionsToIgnore: string[]
  ): IChangeDetails[] {
    const resultArray: IChangeDetails[] = [];

    const isArray = Array.isArray;
    const prefix = keyName != '' ? keyName + ': ' : '';

    for (const key in oldObj) {
      // If a list of items to ignore were passed, check if key in present in ignore list, if found continue to next value
      if (sectionsToIgnore && sectionsToIgnore.find((element) => element == key)) {
        continue;
      }

      if (isArray(oldObj[key]) && isArray(newObj[key])) {
        if (computeArrayDiff) {
          const arrayDifference = this.arrayDiff(oldObj[key], newObj[key]);

          // Values in Old array are ones which are removed from object
          arrayDifference.oldValuesArray.forEach((element: object) => {
            const arrayObj: IChangeDetails = {
              property: prefix ? prefix : element,
              originalValue: key,
              newValue: '',
              operation: 'Remove',
            };
            resultArray.push(arrayObj);
          });

          // Values in new array are ones which are added from object
          arrayDifference.newValuesArray.forEach((element: object) => {
            const arrayObj: IChangeDetails = {
              property: prefix ? prefix : key,
              originalValue: '',
              newValue: element,
              operation: 'Add',
            };
            resultArray.push(arrayObj);
          });
        }
      }
      // Compare values for changes in date value
      else if (
        this.isDate(oldObj[key]) &&
        this.isDate(newObj[key]) &&
        oldObj[key].getTime() !== newObj[key].getTime()
      ) {
        const arrayObj: IChangeDetails = {
          property: key,
          originalValue: moment(oldObj[key]).format('DD MMM YY h:mma'),
          newValue: moment(newObj[key]).format('DD MMM YY h:mma'),
          operation: 'Change',
        };
        resultArray.push(arrayObj);
      } else if (this.isObject(oldObj[key]) && this.isObject(newObj[key])) {
        //Incase of object, calling method again to compute difference of inner properties
        const responseArray = this.computeObjectDifference(
          oldObj[key],
          newObj[key],
          key,
          computeArrayDiff,
          sectionsToIgnore
        );

        if (responseArray.length > 0) {
          resultArray.push(...responseArray);
        }
      }
      //Check if an old value was removed from the property
      else if (oldObj[key] && !newObj[key]) {
        const arrayObj: IChangeDetails = {
          property: oldObj[key],
          originalValue: key,
          newValue: '',
          operation: 'Remove',
        };
        resultArray.push(arrayObj);
      }
      //Check if an new value was added to object
      else if (oldObj[key] !== newObj[key] && newObj[key]) {
        const arrayObj: IChangeDetails = {
          property: key,
          originalValue: oldObj[key],
          newValue: newObj[key],
          operation: 'Change',
        };
        resultArray.push(arrayObj);
      }

      // Check if any new values were added to Event
      else if (oldObj[key] === undefined && newObj[key]) {
        const arrayObj: IChangeDetails = {
          property: key,
          originalValue: '',
          newValue: newObj[key],
          operation: 'Add',
        };
        resultArray.push(arrayObj);
      }
    }

    return resultArray;
  }

  public addToArray(
    modifiedValue: IChangeDetails[],
    property: object | string,
    originalValue: object | string,
    newValue: object | string,
    operation: string,
    ignoreIfPresent = false
  ) {
    // If value is already present in array, update the value as user would have changes the value in UI
    const obj = modifiedValue.find((x, i, array) => {
      switch (operation) {
        case 'Add':
          if (x.property === property && x.newValue === newValue) {
            if (ignoreIfPresent) {
              array[i] = {
                property: property,
                originalValue: originalValue,
                newValue: newValue,
                operation: operation,
              };
            }

            return true;
          }
          break;
        default:
          if (x.property === property && x.originalValue === originalValue) {
            if (ignoreIfPresent) {
              array[i] = {
                property: property,
                originalValue: originalValue,
                newValue: newValue,
                operation: operation,
              };
            }

            return true;
          }
      }

      return false;
    });

    // If value is not present, add it to the array
    if (!obj) {
      modifiedValue.push({
        property,
        originalValue,
        newValue,
        operation,
      });
    }

    return modifiedValue;
  }

  /**
   * 1. Determine whether 2 objects are similar or not.
   * 2. Iterates through each field and compares the intial and new value.
   * 3. Returns true, when there's no difference.
   */
  public hasIdenticalData(newDetails: any, initialDetails: any): boolean {
    const HK = '$$hashKey';

    // Extract keys from the new object
    const keys = Object.keys(newDetails);

    // Iterate through each key
    const areIdentical = keys.every((item) => {
      /**
       * angular adds $$hashKey property to object/list, to keep track of changes, so it knows when it needs to update the DOM.
       * Do not compare $$hashKey since, it's initial and new value always differ.
       */
      if (item === HK) return true;

      const initialValue = initialDetails ? initialDetails[item] : null;
      const newValue = newDetails[item];

      const isInitialValueValid = this.hasValidValue(initialValue);
      const isNewValueValid = this.hasValidValue(newValue);

      // Skip comparison, when either initial or new value have invalid data such as null, undefined or string.
      if (!isInitialValueValid && !isNewValueValid) return true;

      /**
       * By default, some boolean fields are set to true eg. In customersCovid[], isValid=true.
       * Skip comparison, when a boolean field has an invalid intital value.
       */
      if (!isInitialValueValid && typeof newValue === 'boolean') return true;

      if (initialValue instanceof Date && newValue instanceof Date)
        return initialValue.getTime() === newValue.getTime();

      const isComparingArrays = initialValue instanceof Array && newValue instanceof Array;

      if (isComparingArrays) {
        // first does a simply array length check
        if (initialValue.length > 0 && initialValue.length !== newValue.length) return false;
      }

      let valuesAreEqual;
      if (isComparingArrays && initialValue.length && isObject(initialValue[0])) {
        // booking options were getting through because they were arrays with objects, so added a check for $$hashKey in array of objects
        const deleteHKFromArray = (arr: any[]) => {
          arr.map((o: any) => {
            if (o[HK]) {
              delete o[HK];
            }
            return o;
          });
          return arr;
        };
        const newValueArrayClean = deleteHKFromArray(newValue);
        const initialValueArrayClean = deleteHKFromArray(initialValue);
        valuesAreEqual = isEqual(newValueArrayClean, initialValueArrayClean);
      } else {
        valuesAreEqual = isEqual(newValue, initialValue);
      }

      // Deep comparison between 2 values
      if (!valuesAreEqual) {
        // If it's an object/array, iterate through each of it's properties/rows again
        /**
         * To do
         * horita
         * Need to check this
         */
        return isObject(newValue) ? this.hasIdenticalData(newValue, initialValue) : false;
      }

      return true;
    });

    return areIdentical;
  }

  /**
   * Determines whether the provided value is valid or not.
   */
  private hasValidValue(value: any): boolean {
    if (value === undefined) return false;
    if (value === null) return false;
    if (value === '') return false;
    if (value instanceof Array && value.length === 0) return false;
    return true;
  }
}

const instance = new CompareObjectsService();
export default instance;
